import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useParams, useSearchParams } from 'react-router';
import service from '../services/service';
import { usePaths } from '../components/Routes/RouterList';
import { buildLocale } from '../utils/buildLocale';
import { actionsOther, selectorsOther } from '../redux/other/otherReducer';
import { selectorsLocale } from '../redux/locale/localeReducer';
import { useLessThen991 } from '../utils/mediaQuery';
import HeaderLogo from '../components/common/HeaderLogo';
import ClipLoader from 'react-spinners/ClipLoader';
import { setLoaderColor } from '../utils/setLoaderColor';
import { IOrderReview } from '../typings/IOrderReview';
import OrderFeedbackItemComponent, { OrderFeedbackItem } from '../components/OrderFeedback/OrderFeedbackItemComponent';
import { useNavigate } from 'react-router';

const OrderFeedbackPage = () => {
  const params: any = useParams();
  const orderId = Number(params.id);
  const [searchParams] = useSearchParams();
  const checkSum = searchParams.get('cs') || '';
  const navigate = useNavigate();
  const paths = usePaths();
  const dispatch = useDispatch<any>();
  const isDesktop = useSelector(selectorsOther.isDesktop);
  const isLess991 = useLessThen991(isDesktop);
  const currentTranslate = useSelector(selectorsLocale.getTranslate);
  const [isLoading, setIsLoading] = useState(true);
  const [isFeedbackAlreadyExists, setIsFeedbackAlreadyExists] = useState(false);
  const [isThanksForOrderFeedback, setIsThanksForOrderFeedback] = useState(false);
  const [isButtonAvailable, setIsButtonAvailable] = useState(false);
  const [orderFeedbackData, setOrderFeedbackData] = useState<IOrderReview>({
    productsRating: 0,
    productsRatingComment: null,
    courierRating: 0,
    courierRatingComment: null,
    serviceRating: 0,
    serviceRatingComment: null,
    comment: null,
  });
  const availableFeedbackBlocksData: { key: string; titleKey: string }[] = [
    { key: 'productsRating', titleKey: 'qualityForProducts' },
    {
      key: 'courierRating',
      titleKey: 'carrierFeedback',
    },
    { key: 'serviceRating', titleKey: 'serviceFeedback' },
  ];

  const validateChecksum = (checksum: string) => {
    setIsLoading(true);
    service
      .validateOrderFeedbackChecksum(orderId, checksum)
      .then((res) => {
        if (res.success) {
          setIsLoading(false);
          return;
        }
        if (res.code === 409) {
          setIsFeedbackAlreadyExists(true);
          setIsLoading(false);
          return;
        }
        dispatch(actionsOther.setData({ is404Error: true }));
      })
      .catch(() => setIsLoading(false));
  };

  useEffect(() => {
    if (!checkSum?.length) {
      dispatch(actionsOther.setData({ is404Error: true }));
      return;
    }
    validateChecksum(checkSum);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkSum]);

  const onSubmit = () => {
    setIsLoading(true);
    service
      .addOrderFeedback(orderId, checkSum, orderFeedbackData)
      .then((res) => {
        if (res.success) {
          setIsThanksForOrderFeedback(true);
          return;
        }
        if (res.code === 409) {
          setIsFeedbackAlreadyExists(true);
          return;
        }
        dispatch(actionsOther.setData({ is404Error: true }));
      })
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    validateFeedbackData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderFeedbackData]);

  const validateFeedbackData = () => {
    const isValid = availableFeedbackBlocksData.some(
      (data: { key: string; titleKey: string }) => orderFeedbackData[data.key] > 0,
    );
    setIsButtonAvailable(isValid);
  };

  const updateFeedbackItemData = (feedbackBlockKey: string, data: OrderFeedbackItem) => {
    setOrderFeedbackData((prevData: any) => ({
      ...prevData,
      [feedbackBlockKey]: data.rating,
      [`${feedbackBlockKey}Comment`]: data.text?.length ? data.text : null,
    }));
  };

  const updateOverallComment = (value: string) => {
    setOrderFeedbackData((prevData: any) => ({ ...prevData, comment: value?.length ? value : null }));
  };

  return (
    <section className="order-feedback-page">
      <header className="order-feedback-page_header">
        <HeaderLogo />
      </header>
      {isLoading && (
        <div className="order-feedback-loader">
          <ClipLoader size={50} color={setLoaderColor()} loading={true} />
        </div>
      )}
      {!isLoading && (
        <div className="container">
          <h1>
            {buildLocale(currentTranslate, 'reviewOrderTitle')} №{orderId}
          </h1>
          <div className="order-feedback-block">
            {!isFeedbackAlreadyExists && !isThanksForOrderFeedback && (
              <>
                {availableFeedbackBlocksData.map((blockData: { key: string; titleKey: string }) => (
                  <div className="order-feedback-block_one">
                    <OrderFeedbackItemComponent
                      itemBlockTitle={buildLocale(currentTranslate, blockData.titleKey)}
                      updateFeedbackItemData={(data: OrderFeedbackItem) => updateFeedbackItemData(blockData.key, data)}
                    />
                  </div>
                ))}
                <div className="order-feedback-block_one">
                  <label>{buildLocale(currentTranslate, 'overallComment')}</label>
                  <textarea className="form-control" onChange={(e) => updateOverallComment(e.target.value)} />
                </div>
                <div className="order-feedback-total">
                  <button className="btn" onClick={onSubmit} disabled={!isButtonAvailable}>
                    {buildLocale(currentTranslate, 'btnSave')}
                  </button>
                </div>
              </>
            )}
            {isFeedbackAlreadyExists && (
              <div className="order-feedback-block_one">
                <p className="order-feedback-message">
                  <span className="icon">
                    <span className="icon-check"></span>
                  </span>
                  <span>{buildLocale(currentTranslate, 'orderFeedbackAlreadyExists')}</span>
                </p>
                <div className="order-feedback-total">
                  <button className="btn go-to-shopping" onClick={() => navigate(paths.home)}>
                    {buildLocale(currentTranslate, 'basketEmpty2')}
                  </button>
                </div>
              </div>
            )}
            {isThanksForOrderFeedback && (
              <div className="order-feedback-block_one">
                <p className="order-feedback-message">
                  <span className="icon">
                    <span className="icon-check-success"></span>
                  </span>
                  <span>{buildLocale(currentTranslate, 'thanksForOrderFeedback')}</span>
                </p>
                <div className="order-feedback-total">
                  <button className="btn go-to-shopping" onClick={() => navigate(paths.home)}>
                    {buildLocale(currentTranslate, 'basketEmpty2')}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      {!isLess991 && (
        <ul className="footer-menu">
          <li>
            <Link to={paths.about_us}>{buildLocale(currentTranslate, 'footerAboutUs')}</Link>
          </li>
          <li>
            <Link to={paths.regional_products}>{buildLocale(currentTranslate, 'footerRegionalProducts')}</Link>
          </li>
          <li>
            <Link to={paths.regional_partners}>{buildLocale(currentTranslate, 'footerRegionalPartners')}</Link>
          </li>
          <li>
            <Link to={paths.brands}>{buildLocale(currentTranslate, 'brandsFooter')}</Link>
          </li>
          <li>
            <Link to={paths.bio_badges}>{buildLocale(currentTranslate, 'footerBioBadges')}</Link>
          </li>
          <li>
            <Link to={paths.delivery}>{buildLocale(currentTranslate, 'footerDelivery')}</Link>
          </li>
          <li>
            <Link to={paths.produktrueckrufe}>{buildLocale(currentTranslate, 'footerProduktrueckrufe')}</Link>
          </li>
          <li>
            <Link to={paths.presse}>{buildLocale(currentTranslate, 'footerPresse')}</Link>
          </li>
          <li>
            <a href="https://join.com/companies/mybio" target="_blank" rel="noreferrer">
              {buildLocale(currentTranslate, 'footerCarreer')}
            </a>
          </li>
          <li>
            <Link to={paths.faq}>{buildLocale(currentTranslate, 'footerFaq')}</Link>
          </li>
          <li>
            <Link to={paths.agb}>{buildLocale(currentTranslate, 'footerAgb')}</Link>
          </li>
          <li>
            <Link to={paths.widerrufsrecht}>{buildLocale(currentTranslate, 'footerWiderrufsrecht')}</Link>
          </li>
          <li>
            <Link to={paths.confidentiality}>{buildLocale(currentTranslate, 'footerConfid')}</Link>
          </li>
          <li>
            <Link to={paths.impressum}>{buildLocale(currentTranslate, 'footerImpressum')}</Link>
          </li>
        </ul>
      )}
    </section>
  );
};

export default OrderFeedbackPage;
